import { ArrowTrendingUpIcon, ClockIcon } from "@heroicons/react/20/solid";
import { useQuery } from "@tanstack/react-query";
import clsx from "clsx";
import { useMemo } from "react";
import { NavLink } from "react-router-dom";

import CardValueFullWidthWithOptionalBorder
	from "~/components/Card/components/CardValueFullWidthWithOptionalBorder.tsx";
import Headline from "~/components/Headline/Headline.tsx";
import Hint from "~/components/Hint";
import LoadingSpinner from "~/components/LoadingSpinners/LoadingSpinner.tsx";
import Tooltip from "~/components/Tooltip.tsx";
import { QUOTA_DETAILS_PATH_WITH_ORIGIN } from "~/constants/appRoutes.ts";
import { quotaQueries } from "~/modules/quota/api/quota/quotaQueries.ts";
import type { OrderQuotaConsumer, Quota } from "~/modules/quota/api/quota/quotaTypes.ts";
import {
	formatQuotaUsablePeriod,
	validQuotaDetailsNavigatedFromOrigins,
} from "~/modules/quota/components/QuotaDetailsView/quotaDetailsUtils.ts";
import {
	getOverlappingBookableQuotaConsumers,
	quotaConsumerTakesBookings,
} from "~/modules/quota/utils/quotaOverlapUtils.ts";
import { formatCentsToCurrency } from "~/utils/currencyUtils.ts";

type Props = {
	orderId: string;
	projectId: string;
};

function getQuotaDetailsPath(quota: Quota, projectId: string): string {
	return QUOTA_DETAILS_PATH_WITH_ORIGIN(quota.companyId, quota.id, validQuotaDetailsNavigatedFromOrigins.PROJECT, { projectId });
}

// The tooltip of the booking icon. It names the first reason why the order cannot book to the quota.
function getTimeTrackingStatusText(consumer: OrderQuotaConsumer): string {
	if (!consumer.quota.isBookable) return "Zeiterfassung inaktiv: Kontingent geschlossen";
	if (!consumer.timeTrackingEnabled) return "Zeiterfassung inaktiv: für diese Bestellung deaktiviert";
	return "Zeiterfassung aktiv";
}

// The quota tab of the order details. It lists each quota the order consumes with the order's budget
// figures. It warns when two or more quotas can take the same tracking, because the time tracking form
// then disables the booking to a quota.
export const OrderQuotasSection = ({ orderId, projectId }: Props) => {
	const { data: quotaConsumers, isLoading } = useQuery(quotaQueries.byOrder(projectId, orderId));

	const overlappingConsumers = useMemo(() => getOverlappingBookableQuotaConsumers(quotaConsumers ?? []),
		[quotaConsumers]);

	if (isLoading) {
		return <div className="flex justify-center px-4 py-6">
			<LoadingSpinner theme="neutral" />
		</div>;
	}

	if (!quotaConsumers || quotaConsumers.length === 0) {
		return <div className="px-4 py-3 text-sm text-gray-500">Diese Bestellung ist keinem Kontingent zugeordnet.</div>;
	}

	return <div className="space-y-4 px-4 py-3">
		{overlappingConsumers.length > 0 && <Hint theme="warning"
												  size="sm">
			<p className="whitespace-pre-wrap break-words">Dieser Bestellung sind mehrere aktive Kontingente mit
				überschneidender Gültigkeit zugewiesen. In der Zeiterfassung kann daher auf keines davon gebucht
				werden. Bitte auf der Detailseite der Kontingente die Zeiterfassung deaktivieren oder die
				Gültigkeit anpassen.</p>
		</Hint>}
		{quotaConsumers.map((consumer) => <NavLink key={consumer.id}
												   to={getQuotaDetailsPath(consumer.quota, projectId)}
												   className="group flex items-center justify-between gap-x-4 rounded-lg border border-gray-200 bg-white px-4 hover:bg-gray-100">
			<div>
				<Headline type="h5"
						  color="muted">{consumer.quota.title}</Headline>
				<div className="text-sm text-gray-500">
					{`Gültigkeit: ${formatQuotaUsablePeriod(consumer.quota.usableFrom, consumer.quota.usableUntil)}`}
					{!consumer.quota.isBookable && " · Geschlossen"}
				</div>
			</div>
			<div className="ml-auto" />
			<div>
				{/* A null slice means the order shares the quota's unassigned pot. */}
				<CardValueFullWidthWithOptionalBorder label="Zugewiesen"
													  hasBottomBorder={false}><span className="font-bold text-gray-700">{consumer.budgetCentsAssigned !== null ? formatCentsToCurrency(consumer.budgetCentsAssigned) : "Geteilt"}</span></CardValueFullWidthWithOptionalBorder>
			</div>
			<div>
				<CardValueFullWidthWithOptionalBorder label="Verfügt"
													  hasBottomBorder={false}><span className="font-bold text-gray-700">{formatCentsToCurrency(consumer.budgetCentsConsumed)}</span></CardValueFullWidthWithOptionalBorder>
			</div>
			<div>
				<CardValueFullWidthWithOptionalBorder label="Verfügbar"
													  hasBottomBorder={false}><span className="font-bold text-gray-700">{formatCentsToCurrency(consumer.remainingCents)}</span></CardValueFullWidthWithOptionalBorder>
			</div>
			<Tooltip text={consumer.topsUpBudget ? "Erhöht das Budget der Bestellung" : "Erhöht das Budget der Bestellung nicht"}>
				<span>
					<ArrowTrendingUpIcon className={clsx("size-6", consumer.topsUpBudget ? "text-success-500" : "text-gray-300")} />
				</span>
			</Tooltip>
			<Tooltip text={getTimeTrackingStatusText(consumer)}>
				<span>
					<ClockIcon className={clsx("size-6", quotaConsumerTakesBookings(consumer) ? "text-success-500" : "text-danger-500")} />
				</span>
			</Tooltip>
		</NavLink>)}
	</div>;
};
